import { initializeApp } from 'firebase/app';
import { type Messaging, getMessaging, getToken, onMessage } from 'firebase/messaging';
import { firebaseConfig, vapidKey } from './firebase-config.js';

export class FCMClient {
	private messaging: Messaging | null = null;
	private isInitialized = false;

	/**
	 * Initialize Firebase and FCM
	 */
	async initialize(): Promise<void> {
		if (this.isInitialized) {
			return;
		}

		try {
			// Initialize Firebase
			const app = initializeApp(firebaseConfig);

			// Initialize Cloud Messaging
			this.messaging = getMessaging(app);

			this.isInitialized = true;
			console.log('FCM initialized successfully');
		} catch (error) {
			console.error('Failed to initialize FCM:', error);
			throw error;
		}
	}

	/**
	 * Request notification permission and get FCM token
	 */
	async requestPermissionAndGetToken(): Promise<string | null> {
		try {
			// Check if notifications are supported
			if (!('Notification' in window)) {
				console.error('This browser does not support notifications');
				return null;
			}

			// Request permission
			const permission = await Notification.requestPermission();

			if (permission !== 'granted') {
				console.warn('Notification permission denied');
				return null;
			}

			// Ensure FCM is initialized
			if (!this.messaging) {
				await this.initialize();
			}

			if (!this.messaging) {
				throw new Error('Messaging not initialized');
			}

			// Get FCM token
			const token = await getToken(this.messaging, {
				vapidKey,
			});

			if (token) {
				console.log('FCM token retrieved:', `${token.substring(0, 20)}...`);
				return token;
			}

			console.warn('No registration token available');
			return null;
		} catch (error) {
			console.error('Failed to get FCM token:', error);
			throw error;
		}
	}

	/**
	 * Set up foreground message handler
	 */
	setupForegroundMessageHandler(callback: (payload: unknown) => void): void {
		if (!this.messaging) {
			console.error('Messaging not initialized');
			return;
		}

		onMessage(this.messaging, (payload) => {
			console.log('Foreground message received:', payload);
			callback(payload);
		});
	}

	/**
	 * Register service worker for background notifications
	 */
	async registerServiceWorker(): Promise<void> {
		try {
			if ('serviceWorker' in navigator) {
				const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
				console.log('Service Worker registered:', registration);
			}
		} catch (error) {
			console.error('Service Worker registration failed:', error);
		}
	}

	/**
	 * Check if notification permission is granted
	 */
	isPermissionGranted(): boolean {
		return 'Notification' in window && Notification.permission === 'granted';
	}

	/**
	 * Check if FCM is supported
	 */
	isSupported(): boolean {
		return 'Notification' in window && 'serviceWorker' in navigator;
	}
}
